import { useEffect, useState } from 'react';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { CpeInterfaces } from '@bumble/app/types/cpeTypes';
import { CELLULAR_PROD_CODES, CPE_MODE_PRIVATE_LINE } from '@bumble/app/constants';

interface RouterNetworkInfoEditProps {
  onSubmit: () => void;
  cpeId: string;
  prodCode: string;
  mode: string;
  ports: CpeInterfaces | undefined;
  additionalLanPorts: any;
}

export const RouterNetworkInfoEdit = (
  { onSubmit, cpeId, prodCode, mode, ports, additionalLanPorts }: RouterNetworkInfoEditProps
) => {
  const api = useApi();
  const [routerMode, setRouterMode] = useState<boolean>(mode === 'ROUTER');
  // Set at creation and never changed: only the link speeds are editable.
  const privateLine = mode === CPE_MODE_PRIVATE_LINE;
  const [newDhcpClient, setNewDhcpClient] = useState<boolean>(false);
  const [newCidr, setNewCidr] = useState<string>('');
  const [newGwIp, setNewGwIp] = useState<string>('');
  const [newWanDnsIp, setNewWanDnsIp] = useState<string>('');
  const [newSnat, setNewSnat] = useState<boolean>(false);
  const [newLinkUpSpeed, setNewLinkUpSpeed] = useState<string>('0');
  const [newLinkDownSpeed, setNewLinkDownSpeed] = useState<string>('0');
  const [newLanCidr, setNewLanCidr] = useState<string>('');
  const [newDhcpServer, setNewDhcpServer] = useState<boolean>(false);
  const [newDhcpIpS, setNewDhcpIpS] = useState<string>('');
  const [newDhcpIpE, setNewDhcpIpE] = useState<string>('');
  const [newDnsIp, setNewDnsIp] = useState<string>('');
  const [enableVlanSetting, setEnableVlanSetting] = useState<boolean>(false);
  const [enableVlan, setEnableVlan] = useState<boolean>(false);
  const [vlanData, setVlanData] = useState<string>('');
  const [enableQInQ, setEnableQInQ] = useState<boolean>(false);
  const [qInQData, setQInQData] = useState<string>('');
  const [lanPorts, setLanPorts] = useState(additionalLanPorts ?? []);
  const [wanIntfName, setWanIntfName] = useState<string>('');
  const [lanIntfName, setLanIntfName] = useState<string>('Eth1');
  // SFP port / fiber selections are fixed at creation and shown read-only here.
  const [wanIsSfp, setWanIsSfp] = useState<boolean>(false);
  const [wanSfpType, setWanSfpType] = useState<string>('');
  const [lanIsSfp, setLanIsSfp] = useState<boolean>(false);
  const [lanSfpType, setLanSfpType] = useState<string>('');
  // Activated SIM ICCID, editable only for cellular SKUs ("C" in the code).
  const [activatedIccid, setActivatedIccid] = useState<string>('');

  const passthroughVlanProd =
    prodCode === 'BN-CPE-1000' ||
    prodCode === 'BN-CPE-1000C' ||
    prodCode === 'BN-CPE-1000FC' ||
    prodCode === 'BN-CPE-1000F';

  // Fiber SKUs (letter "F") expose SFP WAN/LAN ports; mirrors RouterCreate's mode 3.
  const fiberProd =
    prodCode === 'BN-CPE-1000F' ||
    prodCode === 'BN-CPE-1000FC' ||
    prodCode === 'BN-CPE-10GF' ||
    prodCode === 'BN-CPE-10GFC';
  // 10G SKUs have SFP+ cages labelled Eth0 (WAN) / Eth1 (LAN); 1G fiber uses Eth4 / Eth5.
  const tenG = prodCode === 'BN-CPE-10GF' || prodCode === 'BN-CPE-10GFC';
  // Cellular SKUs carry a SIM; its activated ICCID is editable here.
  const cellularProd = CELLULAR_PROD_CODES.includes(prodCode);
  // Backend stores the fiber type as multi_mode_fiber / single_mode_fiber.
  const sfpRadioValue = (t: string) => (t === 'single_mode_fiber' ? 'sfp_smf' : 'sfp_mmf');

  useEffect(() => {
    if (ports) {
      let wan: any = {}, lan: any = {}, mgmt: any = {}, cellular: any = {};
      for (let itf in ports) {
        const role = ports[itf]?.role?.toLowerCase() ?? '';
        if (role.startsWith('lan0')) {
          lan = ports[itf];
        }
        if (role.startsWith('wan0')) {
          wan = ports[itf];
        }
        if (role.startsWith('cellular')) {
          cellular = ports[itf];
        }
        if (itf === 'mgmt') mgmt = ports[itf];
      }
      setWanIntfName(wan['name'] ?? '');
      setLanIntfName(lan['name'] ?? '');
      setActivatedIccid(cellular.activated_iccid || '');
      setWanIsSfp(!!wan.sfp);
      setWanSfpType(wan.sfp_type || '');
      setLanIsSfp(!!lan.sfp);
      setLanSfpType(lan.sfp_type || '');
      setNewLinkUpSpeed(wan.tx_mbps || '0');
      setNewLinkDownSpeed(wan.rx_mbps || '0');
      if (mode === 'PASSTHROUGH') {
        setNewDhcpClient(mgmt.dhcp ? true : false);
        setNewCidr(mgmt.cidr_addr || '');
        setNewGwIp(mgmt.default_gw_ip || '');
        setNewWanDnsIp(mgmt.dns_ip || '');
        if (passthroughVlanProd) {
          const p = ports as any;
          const vlanPort =
            p.eth0?.vlan_ids?.data != null
              ? p.eth0
              : p.eth4?.vlan_ids?.data != null
                ? p.eth4
                : p.eth0 ?? p.eth4;
          const vlanIds = vlanPort?.vlan_ids;
          const dataVlan = vlanIds?.data != null ? Number(vlanIds.data) : 0;
          const qInQV = vlanIds?.q_in_q != null ? Number(vlanIds.q_in_q) : 0;
          if (dataVlan > 0 || qInQV > 0) {
            setEnableVlanSetting(true);
            setEnableVlan(dataVlan > 0);
            setEnableQInQ(qInQV > 0);
            setVlanData(dataVlan > 0 ? String(dataVlan) : '');
            setQInQData(qInQV > 0 ? String(qInQV) : '');
          } else {
            setEnableVlanSetting(false);
            setEnableVlan(false);
            setEnableQInQ(false);
            setVlanData('');
            setQInQData('');
          }
        }
      } else {
        setNewDhcpClient(wan.dhcp ? true : false);
        setNewCidr(wan.cidr_addr || '');
        setNewGwIp(wan.default_gw_ip || '');
        setNewWanDnsIp(wan.dns_ip || '');
        setNewSnat(wan.snat ? true : false);
        setNewLanCidr(lan.cidr_addr || '');
        setNewDhcpServer(lan.dhcp ? true : false);
        setNewDhcpIpS(lan.dhcp && lan.dhcp_ip_s ? lan.dhcp_ip_s : '');
        setNewDhcpIpE(lan.dhcp && lan.dhcp_ip_e ? lan.dhcp_ip_e : '');
        setNewDnsIp(lan.dhcp ? lan.dns_ip : '');
        if (passthroughVlanProd) {
          const p = ports as any;
          const vlanPort =
            p.eth0?.vlan_ids?.data != null
              ? p.eth0
              : p.eth4?.vlan_ids?.data != null
                ? p.eth4
                : p.eth0 ?? p.eth4;
          const vlanIds = vlanPort?.vlan_ids;
          const dataVlan = vlanIds?.data != null ? Number(vlanIds.data) : 0;
          if (dataVlan > 0) {
            setEnableVlan(dataVlan > 0);
            setVlanData(dataVlan > 0 ? String(dataVlan) : '');
          } else {
            setEnableVlan(false);
            setVlanData('');
          }
        }
      }
    }
  }, []);

  const cpeInfoUpdatePost = useMutation(api.editCpeNetworkApiV1EditCpeNetworkPost, {
    onSuccess() {
      toast.success('Updated network interface successfully.');
      onSubmit();
    },
    onError: apiError,
  });

  // The API requires both speeds and rejects 0, so a missing value must be filled in here.
  // Checked as parseInt, which is what's sent, so a stored '93.5' still passes.
  const isValidSpeed = (speed: string) => parseInt(speed) > 0;

  const validate = () => {
    if (!isValidSpeed(newLinkUpSpeed)) {
      toast.error('Please enter a link up speed greater than 0.');
      return false;
    }
    if (!isValidSpeed(newLinkDownSpeed)) {
      toast.error('Please enter a link down speed greater than 0.');
      return false;
    }
    return true;
  };

  const handleChangeMode = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setRouterMode(value === 'true');
  };

  const handleSubmit = () => {
    if (validate()) {
      cpeInfoUpdatePost.mutate({
        cpe_id: cpeId,
        wan_tx_mbps: parseInt(newLinkUpSpeed),
        wan_rx_mbps: parseInt(newLinkDownSpeed),
        ...(cellularProd && activatedIccid.trim() && { activated_iccid: activatedIccid.trim() }),
        ...(privateLine
          ? { mode: CPE_MODE_PRIVATE_LINE }
          : routerMode
          ? {
            mode: 'ROUTER',
            wan_dhcp_c: newDhcpClient,
            ...(!newDhcpClient && {
              wan_cidr_addr: newCidr,
              wan_default_gw_ip: newGwIp,
              wan_dns_ip: newWanDnsIp,
            }),
            wan_snat: newSnat,
            ...(newLanCidr && { lan_cidr_addr: newLanCidr }),
            dhcp_server: newDhcpServer,
            ...(newDhcpServer && {
              dhcp_ip_s: newDhcpIpS,
              dhcp_ip_e: newDhcpIpE,
              dns_ip: newDnsIp,
            }),
            additional_lan_ports: lanPorts.filter((p: any) => p.selected).map((p: any) => p.name),
            ...(passthroughVlanProd
              ? {
                  ...(enableVlan && vlanData.trim() !== ''
                    ? { wan_data_vlan_id: parseInt(vlanData, 10) }
                    : {}),
                }
              : {}),
          }
          : {
            mode: 'PASSTHROUGH',
            mgmt_dhcp_c: newDhcpClient,
            ...(!newDhcpClient && {
              mgmt_cidr_addr: newCidr,
              mgmt_default_gw_ip: newGwIp,
              mgmt_dns_ip: newWanDnsIp,
            }),
            ...(passthroughVlanProd
              ? {
                  ...(enableVlanSetting && enableVlan && vlanData.trim() !== ''
                    ? { wan_data_vlan_id: parseInt(vlanData, 10) }
                    : {}),
                  ...(enableVlanSetting && enableQInQ && qInQData.trim() !== ''
                    ? { wan_q_in_q_vlan_id: parseInt(qInQData, 10) }
                    : {}),
                }
              : {}),
          }
        ),
      });
    }
  };

  return (
    <Box pt={2}>
      {prodCode !== 'BN-CPE-IP' && <>
        <TextField
          fullWidth
          sx={{ mb: 4 }}
          label="Link Upload Speed (mbps)"
          variant="outlined"
          value={newLinkUpSpeed}
          onChange={(e) => setNewLinkUpSpeed(e.target.value)}
        />
        <TextField
          fullWidth
          sx={{ mb: 4 }}
          label="Link Download Speed (mbps)"
          variant="outlined"
          value={newLinkDownSpeed}
          onChange={(e) => setNewLinkDownSpeed(e.target.value)}
        />
      </>}
      <Divider sx={{ my: 4 }} />
      {fiberProd && <>
        <Box sx={{ mb: 4 }}>
          <Typography variant="body2" sx={{ mb: 2, display: 'inline-block', mr: '50px' }}>
            WAN Interface:
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={wanIsSfp} disabled />}
            label={tenG ? 'Use SFP+ Port (Eth0)' : 'Use SFP Port (Eth4)'}
          />
          {wanIsSfp && (
            <FormControl component="fieldset" sx={{ mt: 1, display: 'flex', pl: '40px' }} disabled>
              <RadioGroup row value={sfpRadioValue(wanSfpType)}>
                <FormControlLabel value="sfp_mmf" control={<Radio />} label="Multimode Fiber (MMF) (Short range)" />
                <FormControlLabel value="sfp_smf" control={<Radio />} label="Single-Mode Fiber (SMF) (Long range)" />
              </RadioGroup>
            </FormControl>
          )}
        </Box>
        <Box sx={{ mb: 4 }}>
          <Typography variant="body2" sx={{ mb: 2, display: 'inline-block', mr: '55px' }}>
            LAN Interface:
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={lanIsSfp} disabled />}
            label={tenG ? 'Use SFP+ Port (Eth1)' : 'Use SFP Port (Eth5)'}
          />
          {lanIsSfp && (
            <FormControl component="fieldset" sx={{ mt: 1, display: 'flex', pl: '40px' }} disabled>
              <RadioGroup row value={sfpRadioValue(lanSfpType)}>
                <FormControlLabel value="sfp_mmf" control={<Radio />} label="Multimode Fiber (MMF) (Short range)" />
                <FormControlLabel value="sfp_smf" control={<Radio />} label="Single-Mode Fiber (SMF) (Long range)" />
              </RadioGroup>
            </FormControl>
          )}
        </Box>
        <Divider sx={{ my: 4 }} />
      </>}
      {cellularProd && <>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Cellular Interface
        </Typography>
        <TextField
          fullWidth
          sx={{ mb: 4 }}
          label="Activated ICCID (optional)"
          variant="outlined"
          value={activatedIccid}
          onChange={(e) => setActivatedIccid(e.target.value)}
        />
        <Divider sx={{ my: 4 }} />
      </>}
      {privateLine ? (
        <Typography variant="body2" sx={{ mb: 4 }}>
          Private Line: the circuit{wanIntfName && ` (${wanIntfName})`} and the LAN ports are bridged with no
          address, and the CPE is managed over cellular. The mode is set at creation and
          cannot be changed.
        </Typography>
      ) : <>
      <FormControl fullWidth sx={{ mb: 4 }}>
        <RadioGroup
          row
          name="router-mode"
          onChange={handleChangeMode}
          value={routerMode.toString()}
        >
          <FormControlLabel value="false" control={<Radio />} label="Pass through" />
          <FormControlLabel value="true" control={<Radio />} label="Router mode" />
        </RadioGroup>
      </FormControl>
      <Typography variant="body2" sx={{ mb: 4 }}>
        {routerMode ? `WAN Interface${wanIntfName && ` (${wanIntfName})`}` : 'Management Interface'}
      </Typography>
      <FormControlLabel
        sx={{ mb: 4 }}
        control={
          <Checkbox
            checked={newDhcpClient}
            onChange={(e) => setNewDhcpClient(e.target.checked)}
          />
        }
        label="DHCP Client"
      />
      {!newDhcpClient && <>
        <TextField
          fullWidth
          sx={{ mb: 4 }}
          label="IP Address in CIDR format (Ex: A.B.C.D/24)"
          variant="outlined"
          value={newCidr}
          onChange={(e) => setNewCidr(e.target.value)}
          autoFocus
        />
        <TextField
          fullWidth
          sx={{ mb: 4 }}
          label="Default Gateway IP"
          variant="outlined"
          value={newGwIp}
          onChange={(e) => setNewGwIp(e.target.value)}
        />
        <TextField
          fullWidth
          sx={{ mb: 4 }}
          label="DNS Server IP(s)"
          variant="outlined"
          value={newWanDnsIp}
          onChange={(e) => setNewWanDnsIp(e.target.value)}
        />
      </>}
      {routerMode &&
        <FormControlLabel
          sx={{ mb: 4 }}
          control={
            <Checkbox
              checked={newSnat}
              onChange={(e) => setNewSnat(e.target.checked)}
            />
          }
          label="Enable Source NAT"
        />
      }
      {routerMode && passthroughVlanProd && (
        <Box>
          <FormControlLabel
            sx={{ mb: 4 }}
            control={
              <Checkbox
                checked={enableVlan}
                onChange={(e) => {
                  const on = e.target.checked;
                  setEnableVlan(on);
                  if (!on) setVlanData('');
                }}
              />
            }
            label="Enable VLAN tagging"
          />
          {enableVlan && (
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              label="VLAN ID"
              variant="outlined"
              type="number"
              value={vlanData}
              onChange={(e) => setVlanData(e.target.value)}
            />
          )}
        </Box>
      )}
      {routerMode && <>
        <Divider sx={{ my: 4 }} />
        <Typography variant="body2" sx={{ mb: 8 }}>
          LAN Interface{lanIntfName && ` (${lanIntfName})`}
        </Typography>
        <TextField
          fullWidth
          sx={{ mb: 4 }}
          id="lan_cidr_ip_address"
          label="IP Address in CIDR format (Ex: A.B.C.D/24)"
          variant="outlined"
          value={newLanCidr}
          onChange={(e) => setNewLanCidr(e.target.value)}
        />
        <FormControlLabel
          sx={{ mb: 4 }}
          control={
            <Checkbox
              checked={newDhcpServer}
              onChange={(e) => setNewDhcpServer(e.target.checked)}
            />
          }
          label="DHCP Server"
        />
        {newDhcpServer &&
          <>
            <TextField
              sx={{ mb: 4 }}
              fullWidth
              label="DHCP IP Address Range Start"
              variant="outlined"
              value={newDhcpIpS}
              onChange={(e) => setNewDhcpIpS(e.target.value)}
              autoFocus
            />
            <TextField
              sx={{ mb: 4 }}
              fullWidth
              label="DHCP IP Address Range End"
              variant="outlined"
              value={newDhcpIpE}
              onChange={(e) => setNewDhcpIpE(e.target.value)}
              autoFocus
            />
            <TextField
              sx={{ mb: 4 }}
              fullWidth
              label="DNS Server IP(s)"
              variant="outlined"
              value={newDnsIp}
              onChange={(e) => setNewDnsIp(e.target.value)}
              autoFocus
            />
          </>
        }
        {lanPorts.map((port: any) => (
          <Box key={port.name}>
            <FormControlLabel
              key={port.name}
              sx={{ mb: 4 }}
              control={
                <Checkbox
                  checked={port.selected}
                  onChange={(e) => {
                    setLanPorts((prev: any) =>
                      prev.map((p: any) => p.name === port.name ? { ...p, selected: e.target.checked } : p)
                    );
                  }}
                />
              }
              label={`Include ${port.name}`}
            />
          </Box>
        ))}
      </>}
      {!routerMode && passthroughVlanProd && (
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={enableVlanSetting}
                onChange={(e) => {
                  const on = e.target.checked;
                  setEnableVlanSetting(on);
                  if (!on) {
                    setEnableVlan(false);
                    setEnableQInQ(false);
                    setVlanData('');
                    setQInQData('');
                  }
                }}
              />
            }
            label="Enable UNI NID"
          />
          {enableVlanSetting && (
            <Box
              sx={{
                pl: 3,
                ml: 1,
                my: 3,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <FormControlLabel
                sx={{ m: 0, alignItems: 'center', justifyContent: 'flex-start' }}
                control={
                  <Checkbox
                    checked={enableVlan}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setEnableVlan(on);
                      if (!on) setVlanData('');
                    }}
                  />
                }
                label="Enable VLAN tagging"
              />
              {enableVlan && (
                <TextField
                  fullWidth
                  sx={{ mb: 2 }}
                  label="VLAN ID"
                  variant="outlined"
                  type="number"
                  value={vlanData}
                  onChange={(e) => setVlanData(e.target.value)}
                />
              )}
              <FormControlLabel
                sx={{ m: 0, alignItems: 'center', justifyContent: 'flex-start' }}
                control={
                  <Checkbox
                    checked={enableQInQ}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setEnableQInQ(on);
                      if (!on) setQInQData('');
                    }}
                  />
                }
                label="Enable Q-in-Q VLAN tagging"
              />
              {enableQInQ && (
                <TextField
                  fullWidth
                  sx={{ mb: 2 }}
                  label="Q-in-Q VLAN ID"
                  variant="outlined"
                  type="number"
                  value={qInQData}
                  onChange={(e) => setQInQData(e.target.value)}
                />
              )}
            </Box>
          )}
        </Box>
      )}
      </>}
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button
          variant="outlined"
          onClick={handleSubmit}
          // disabled={!newDhcpClient && (!newCidr || !newGwIp || !newWanDnsIp)}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};
